import { useEffect, useRef, useState } from 'react';

interface Props {
  /** Either an absolute blob:/http URL (optimistic) or a server-relative path. */
  url: string;
  serverUrl: string;
  durationMs: number;
  mine: boolean;
}

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Voice-note player.
 *
 * "Expired" detection deliberately does NOT rely on the <audio> `error`
 * event: WebM blobs produced by MediaRecorder have no duration/cue
 * metadata, so Chrome's media loader emits spurious `error`s while probing
 * even when the file streams fine (HTTP 206). Instead we do one cheap
 * existence probe (1-byte ranged request) against the server file. Only a
 * genuine 404/410 from retention cleanup shows the "expired" chip.
 */
export function AudioBubble({ url, serverUrl, durationMs, mine }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [total, setTotal] = useState(durationMs / 1000);
  const [expired, setExpired] = useState(false);

  const isLocal = /^blob:/.test(url);
  const src = /^(blob:|https?:)/.test(url) ? url : `${serverUrl.replace(/\/$/, '')}${url}`;

  // Existence probe: only a real "file gone" (404/410) marks it expired.
  // Optimistic blob URLs always exist locally — skip the probe for them.
  useEffect(() => {
    setExpired(false);
    setPlaying(false);
    setCur(0);
    setTotal(durationMs / 1000);
    if (isLocal) return;
    let cancelled = false;
    fetch(src, { method: 'GET', headers: { Range: 'bytes=0-0' } })
      .then(r => {
        if (cancelled) return;
        if (r.status === 404 || r.status === 410) setExpired(true);
      })
      .catch(() => {
        /* network blip — keep the player; play() will retry */
      });
    return () => {
      cancelled = true;
    };
  }, [src, isLocal, durationMs]);

  // Playback progress wiring. (No `error` -> expired here, by design.)
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onMeta = () => {
      if (Number.isFinite(a.duration) && a.duration > 0) setTotal(a.duration);
    };
    const onEnd = () => { setPlaying(false); setCur(0); };
    const onPause = () => setPlaying(false);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnd);
    a.addEventListener('pause', onPause);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('pause', onPause);
    };
  }, [src]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a || expired) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      return;
    }
    // MediaRecorder WebM has no duration/cues; with preload="none" Chrome
    // may not have selected the resource yet. Force a load before play so
    // the first click reliably starts playback.
    try {
      if (a.readyState < 2 /* HAVE_CURRENT_DATA */) {
        a.load();
      }
      await a.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
      // Only call it expired if the file is genuinely gone.
      try {
        const r = await fetch(src, { method: 'GET', headers: { Range: 'bytes=0-0' } });
        if (r.status === 404 || r.status === 410) setExpired(true);
      } catch {
        /* network blip — leave the player as-is */
      }
    }
  };

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const a = audioRef.current;
    if (!a || !total) return;
    const t = (Number(e.target.value) / 100) * total;
    a.currentTime = t;
    setCur(t);
  };

  if (expired) {
    return (
      <div className={`flex items-center gap-2 text-xs ${mine ? 'text-white/80' : 'text-gray-500'}`}>
        <span aria-hidden="true">🎙️</span> Voice message (expired)
      </div>
    );
  }

  const pct = total > 0 ? Math.min(100, (cur / total) * 100) : 0;
  const fg = mine ? 'white' : 'var(--vip-primary)';

  return (
    <div className="flex items-center gap-2 min-w-[180px]">
      <audio key={src} ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play'}
        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          mine ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        } transition`}
      >
        {playing ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
        )}
      </button>
      <input
        type="range" min={0} max={100} value={pct} onChange={seek}
        aria-label="Seek"
        className="flex-1 h-1 cursor-pointer"
        style={{ accentColor: fg }}
      />
      <span className={`text-[11px] tabular-nums shrink-0 ${mine ? 'text-white/80' : 'text-gray-500'}`}>
        {fmt(playing || cur ? cur : total)}
      </span>
    </div>
  );
}
